import type { LoaderFunctionArgs } from "react-router-dom";
import { getGymTermsPage } from "@lemonadetraining/web-shared";
import type { GymTermsPageResponse } from "@lemonadetraining/contracts";

/*
 * Kept out of GymTerms.tsx so the routes can declare `loader` statically while
 * the component stays lazy. Supplied through lazy(), react-router waits for the
 * whole page chunk — the markdown parser included — before it will start this
 * call; declared statically, the two run at once.
 */

export type GymTermsLoaderResult =
  | { status: "ok"; data: GymTermsPageResponse }
  | { status: "not-found" }
  | { status: "ambiguous" }
  | { status: "version-unknown" }
  | { status: "error" };

interface CallableError {
  code?: string;
  details?: { reason?: string };
}

/*
 * The server answers a missing gym, a never-published gym and a withdrawn gym
 * with one token so no outside reader can tell a takedown happened. Keep them
 * one state here too.
 */
const resultForError = (error: unknown): GymTermsLoaderResult => {
  const { code, details } = (error ?? {}) as CallableError;

  switch (details?.reason) {
    case "terms_slug_ambiguous":
      return { status: "ambiguous" };
    case "terms_version_unknown":
      return { status: "version-unknown" };
    case "terms_not_published":
      return { status: "not-found" };
    default:
      break;
  }
  // Rejected before any read: a malformed version tag, gym id or slug carries
  // no reason of its own.
  return code === "functions/invalid-argument" ? { status: "not-found" } : { status: "error" };
};

export async function loader({
  params,
  request,
}: LoaderFunctionArgs): Promise<GymTermsLoaderResult> {
  const selector = params.boxID
    ? { boxID: params.boxID }
    : params.page
      ? { pageName: params.page }
      : null;
  if (!selector) return { status: "not-found" };

  // `?v=` rides the query string, so it is on the request and never on params.
  const version = new URL(request.url).searchParams.get("v");

  try {
    const data = (await getGymTermsPage(version ? { ...selector, version } : selector)) as
      GymTermsPageResponse | undefined;
    return data ? { status: "ok", data } : { status: "error" };
  } catch (error) {
    return resultForError(error);
  }
}
